import { gql, useMutation } from '@apollo/client'
import { type NotificationsQuery, useNotificationsQuery } from '@kijiji/generated/graphql-types'
import CloseIcon from '@kijiji/icons/src/icons/Close'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo, useState } from 'react'

import {
  Body,
  DismissButton,
  PriceDropContainer,
  Title,
  ViewNow,
} from '@/components/homepage/price-drop/styled'
import { ImageYAMS } from '@/components/shared/image/ImageYAMS'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'

const MARK_AS_READ = gql`
  mutation MarkAsRead($notificationId: String!) {
    markAsRead(notificationId: $notificationId) {
      statusCode
    }
  }
`

type PriceDropNotification = NonNullable<
  NotificationsQuery['notifications']['notifications'][number]
>

/**
 * Retrieves the most recent unread price drop notification from the notifications data
 *
 * @param {NotificationsQuery | undefined} data - The notifications query data containing all user notifications
 * @returns {PriceDropNotification | null} The most recent unread price drop notification or null if none exists
 */
export const getPriceDropNotification = (
  data: NotificationsQuery | undefined
): PriceDropNotification | null => {
  if (!data) {
    return null
  }

  // sorts and filter notifications by date and type - leaving only unread price_drop type
  const unReadPricedropNotifications = data.notifications.notifications
    .filter(
      (notification): notification is PriceDropNotification =>
        notification !== null &&
        notification !== undefined &&
        notification?.type === 'price_drop' &&
        notification.read === false
    )
    .sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()) // Sort in descending order (newest first)

  // return the first item in the array - the most recent, or null if no notifications
  return unReadPricedropNotifications.length > 0 ? unReadPricedropNotifications[0] : null
}

/**
 * Component that displays price drop notifications to users
 *
 * This component shows a notification card when an item's price has dropped. It implements
 * a notification management system that:
 *
 * 1. Shows only the most recent unread price drop notification
 * 2. Marks notifications as read when viewed or dismissed
 * 3. Tracks view/dismiss events using Google Analytics
 
 *
 * Features:
 * - Automatically fetches notifications on mount
 * - Memoizes notification data and handlers for performance
 * - Tracks user interactions for analytics
 *
 * @returns {JSX.Element | null} The price drop notification card or null if no unread notifications exist
 */
export const PriceDrop = () => {
  const { t } = useTranslation('home')
  const router = useRouter()
  const [isHidden, setIsHidden] = useState(false)

  // get the notifications
  const { data, error: queryError } = useNotificationsQuery({
    ssr: false,
  })

  // Mutation to mark notifications as read
  const [markAsRead, { error: mutationError }] = useMutation(MARK_AS_READ, {
    onError: () => {
      // If marking as read fails, hide the notification anyway
      setIsHidden(true)
    },
  })

  // Memoize the price drop notification to prevent unnecessary recalculations
  const priceDropNotification = useMemo(() => getPriceDropNotification(data), [data])

  // Memoize the dismiss handler to prevent unnecessary re-renders
  const handleDismiss = useCallback(async () => {
    if (priceDropNotification) {
      await markAsRead({ variables: { notificationId: priceDropNotification.id } })
      if (!mutationError) {
        setIsHidden(true)
        trackEvent({ action: GA_EVENT.PriceDropDismiss })
      }
    }
  }, [priceDropNotification, markAsRead, mutationError])

  // Memoize the view now click handler
  const handleViewNowClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      if (priceDropNotification) {
        try {
          await markAsRead({ variables: { notificationId: priceDropNotification.id } })
          trackEvent({ action: GA_EVENT.SelectPromotion, label: 'promotion_name=pricedrop' })
          // Navigate after mutation is complete
          if (priceDropNotification.destinationUrl) {
            router.push(priceDropNotification.destinationUrl)
          }
        } catch {
          // If marking as read fails, don't navigate
        }
      }
    },
    [priceDropNotification, markAsRead, router]
  )

  // If there's an error or no notification, don't render anything
  if (queryError || mutationError || !priceDropNotification || isHidden) {
    return null
  }

  // if they do have a pricedrop notification - lets show it!
  return (
    <PriceDropContainer data-testid="price-drop">
      <ImageYAMS
        alt={priceDropNotification.title}
        data-testid="listing-card-image"
        src={priceDropNotification.pictureUrl}
        width={96}
        height={72}
        extension="WEBP"
      />
      <div>
        <Title>{priceDropNotification.title}</Title>
        <Body>
          {priceDropNotification.body}
          <ViewNow href={priceDropNotification.destinationUrl ?? '#'} onClick={handleViewNowClick}>
            {t('home:price_drop.view_now')}
          </ViewNow>
        </Body>
      </div>
      <DismissButton onClick={handleDismiss} aria-label={t('home:price_drop.dismiss_label')}>
        <CloseIcon aria-hidden />
      </DismissButton>
    </PriceDropContainer>
  )
}
